import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Clock, CheckCircle, Loader2, X } from 'lucide-react';
import { CircularProgress } from './CircularProgress';
import { PixieTask, TASK_STATES, BADGE_TYPES } from './types';

// --- TYPE DEFINITIONS ---

interface PixieTaskItemProps {
  task: PixieTask;
  onCancel?: (taskId: string) => void;
  onAction?: (taskId: string) => void;
  className?: string;
}

// --- HELPER FUNCTIONS ---

const getStateIcon = (state: PixieTask['state']) => {
  const iconProps = { className: 'w-5 h-5 flex-shrink-0' };
  switch (state) {
    case TASK_STATES.PROCESSING:
      return <Loader2 {...iconProps} className={cn(iconProps.className, 'animate-spin text-blue-500')} />;
    case TASK_STATES.QUEUED:
      return <Clock {...iconProps} className="text-gray-400" />;
    case TASK_STATES.COMPLETED:
      return <CheckCircle {...iconProps} className="text-green-500" />;
    default:
      return null;
  }
};

const getStateBadge = (state: PixieTask['state'], timeSaved?: string) => {
  const badgeType = state === TASK_STATES.COMPLETED ? BADGE_TYPES.SAVED : BADGE_TYPES.PENDING;
  if (state !== TASK_STATES.COMPLETED || !timeSaved) return null;

  return (
    <Badge variant={badgeType === BADGE_TYPES.SAVED ? 'success' : 'default'}>
      {timeSaved} Saved
    </Badge>
  );
};

const getActionButton = (state: PixieTask['state'], timeSaved?: string) => {
  if (state !== TASK_STATES.COMPLETED || !timeSaved) return null;
  return <Button variant="outline" size="sm">View Note</Button>;
};

// --- MAIN COMPONENT ---

const PixieTaskItem: React.FC<PixieTaskItemProps> = ({ task, onCancel = () => {}, onAction = () => {}, className = '' }) => {
  if (!task) return null;

  const { id, patient, type, provider, state, progress = 75, timeSaved } = task;

  const StateIcon = getStateIcon(state);
  const StateBadge = getStateBadge(state, timeSaved);
  const ActionButton = getActionButton(state, timeSaved);

  return (
    <div className={cn('flex items-center gap-4 p-4 border-b border-gray-100', className)}>
      {StateIcon}
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-800">{patient}</p>
          {StateBadge}
        </div>
        <p className="text-xs text-gray-500">{type} for {provider}</p>
        {state === TASK_STATES.PROCESSING && (
          <div className="mt-2 flex items-center gap-2">
            <Progress value={progress} className="h-1.5 flex-1" />
            <span className="text-xs font-medium text-gray-600">{progress}%</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {ActionButton ? (
          <div onClick={() => onAction(id)}>{ActionButton}</div>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => onCancel(id)} aria-label="Cancel Task">
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        )}
      </div>
    </div>
  );
};

PixieTaskItem.displayName = 'PixieTaskItem';

export { PixieTaskItem };
